import React, {memo} from 'react'
import {ActivityHotelState} from "../../../../types-enums/activity-hotel";
import {Icon, TruncatedWordList} from "atmosfair-ui";
import {generateCustomerHotelLocationString} from "../../../../utils/string/hotel";
import {useTranslation} from "react-i18next";
import {CommonActivityStringProps, DEFAULT_TRUNCATED_WORDS_LIST_WIDTH_PERCENTAGE} from "../utils";
import '../styles.css'
import StringDisplayFootnoteIcon, {WHICH_FOOTNOTE_TYPE} from "../common/StringDisplayFootnoteIcon";
import {hasExactDates} from "../../../../utils/getter/journey";
import CalculationTierTooltipHint from "../common/CalculationTierTooltipHint";
import {ACTIVITY_ICON_MAPPING} from "../../../../utils/getter/activities";
import {ACTIVITY_TYPE} from "../../../../types-enums/activity";
import {isCo2KgEValueDifferent} from "../../../../utils/check/common";
import {getHotelClassNumberValueForCalculationRequest} from "../../../../utils/getter/hotel";
import {formatCo2ETotalString, WhereToDisplayActivityString} from "../../../../utils/string/common";

interface HotelLocationProps extends CommonActivityStringProps {
    hotel: ActivityHotelState,
    suppressExactDatesHint?: boolean,
}

const HotelLocation = memo(function HotelLocation({
                                                      whereToDisplay,
                                                      hotel,
                                                      textColor,
                                                      truncatedWordsListMaxWidthPercentage = DEFAULT_TRUNCATED_WORDS_LIST_WIDTH_PERCENTAGE,
                                                      journeyWithExactDates,
                                                      suppressExactDatesHint,
                                                      iconColor,
                                                      hideAllIconHints,
                                                      iconSize,
                                                  }: HotelLocationProps
) {
    const {i18n, t} = useTranslation()
    const {resolvedLanguage} = i18n
    const {locationString, separator} = generateCustomerHotelLocationString(hotel, i18n, true, false, whereToDisplay)
    const {
        originalHotelStayDuration,
        informationFromLoadedBundle,
        hotelClass,
        guestCount,
        hotel: hotelResponse,
        common: {originalTotalEmissions, hideLoadBundleInformation, id}
    } = hotel
    const originalHotelHasExactDates = hasExactDates(originalHotelStayDuration)

    const _renderDifferentCo2KgEValuesOnLoadHint = () => {
        if (informationFromLoadedBundle !== null && originalTotalEmissions !== null) {
            const {previousEmissionsCo2KgE, numberNights} = informationFromLoadedBundle
            const currentEmissionsCo2KgE = originalTotalEmissions[0].emissionKgCO2e
            if (
                currentEmissionsCo2KgE !== null &&
                previousEmissionsCo2KgE !== null &&
                isCo2KgEValueDifferent(currentEmissionsCo2KgE, previousEmissionsCo2KgE)
            ) {
                const previousEmissionsCo2KgEPerDay = previousEmissionsCo2KgE / numberNights
                return <StringDisplayFootnoteIcon
                    tooltip={t("HINTS.EMISSION_CALCULATION.COMMON.DIFFERENT_CALCULATED_CO_KG_E", {
                        previousEmissionsCo2KgE: formatCo2ETotalString(previousEmissionsCo2KgEPerDay, resolvedLanguage),
                        currentEmissionsCo2KgE: formatCo2ETotalString(currentEmissionsCo2KgE, resolvedLanguage),
                    })}
                    iconColor={iconColor}
                    whereToDisplay={whereToDisplay}
                    hideOnClick={true}
                    activityId={id}
                    whichFootnoteType={WHICH_FOOTNOTE_TYPE.calculation}/>

            }
        }
    }

    const _generateLoadedHotelHint = () => {
        if (informationFromLoadedBundle !== null) {
            const tooltipString: string[] = [t("HINTS.BUNDLE.SAVED_DATA"), t("HINTS.BUNDLE.CALCULATED_WITH_NEW_DATA")]
            let showHint = false
            const {hotelDiscontinued, originalHotelName, originalHotelClass} = informationFromLoadedBundle
            if (hotelDiscontinued && originalTotalEmissions !== null) {
                tooltipString.splice(tooltipString.length - 1, 0, t("HINTS.BUNDLE.HOTEL_DISCONTINUED", {originalHotelName}));
                return <StringDisplayFootnoteIcon
                    tooltip={tooltipString.join(" ")}
                    iconColor={iconColor}
                    whereToDisplay={whereToDisplay}
                    hideOnClick={true}
                    activityId={id}
                    whichFootnoteType={WHICH_FOOTNOTE_TYPE.warning}/>
            }

            if (originalHotelName !== null && hotelResponse !== null) {
                showHint = true
                tooltipString.splice(tooltipString.length - 1, 0, t("HINTS.BUNDLE.HOTEL_NEW_NAME", {
                    originalHotelName,
                    newHotelName: hotelResponse.name,
                }));
            }

            if (originalHotelClass !== null && originalHotelClass !== hotelClass) {
                showHint = true
                tooltipString.splice(tooltipString.length - 1, 0, t("HINTS.BUNDLE.HOTEL_CLASS_CHANGED", {
                    originalHotelClass: getHotelClassNumberValueForCalculationRequest(originalHotelClass),
                    newHotelClass: getHotelClassNumberValueForCalculationRequest(hotelClass),
                }));
            }
            if (showHint) return <StringDisplayFootnoteIcon
                tooltip={tooltipString.join(" ")}
                iconColor={iconColor}
                whereToDisplay={whereToDisplay}
                hideOnClick={true}
                activityId={id}
                whichFootnoteType={WHICH_FOOTNOTE_TYPE.warning}/>
        }
    }

    return <div
        className={'activity-string-display-container'}
        style={{maxWidth: `${truncatedWordsListMaxWidthPercentage}%`}}>
        <Icon
            containerClasses={"activity-string-display-activity-icon-margin"}
            size={iconSize ?? "small"}
            color={iconColor}
            icon={ACTIVITY_ICON_MAPPING[ACTIVITY_TYPE.hotel]}/>
        <TruncatedWordList
            color={textColor}
            separator={separator}>
            {`${guestCount} ${guestCount === 1 ? t("GENERAL.PERSON") : t("GENERAL.PERSONS")}${separator} ${locationString}`}
        </TruncatedWordList>
        {
            !suppressExactDatesHint &&
            !hideAllIconHints &&
            whereToDisplay === WhereToDisplayActivityString.journey &&
            !journeyWithExactDates &&
            originalHotelHasExactDates &&
            <StringDisplayFootnoteIcon
                tooltip={t("HINTS.JOURNEY.HOTEL_DATES_REMOVED")}
                iconColor={iconColor}
                whereToDisplay={whereToDisplay}
                whichFootnoteType={WHICH_FOOTNOTE_TYPE.date}/>
        }
        {!hideAllIconHints && _generateLoadedHotelHint()}
        {!hideAllIconHints && !hideLoadBundleInformation && _renderDifferentCo2KgEValuesOnLoadHint()}
        {<CalculationTierTooltipHint iconColor={iconColor} activity={hotel}/>}
    </div>
})

export default HotelLocation